import { sdk } from '@/lib/api';
import { FetchError } from '@/lib/api/client';
import { queryKeysFactory } from '@/lib/query-key-factory';
import { HttpTypes } from '@/types';
import {
  QueryKey,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import Cookies from 'js-cookie';

const BLOG_QUERY_KEY = 'blog-list' as const;
const BLOG_PREVIEW_QUERY_KEY = 'blog-preview-list' as const;

export const blogQueryKeys = queryKeysFactory(BLOG_QUERY_KEY);
export const blogPreviewQueryKeys = queryKeysFactory(BLOG_PREVIEW_QUERY_KEY);

const isPartner = Cookies.get('_partner_id') !== undefined;

export const useBlogs = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminBlogListResponse,
      FetchError,
      HttpTypes.AdminBlogListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: blogQueryKeys.list(query),
    queryFn: async () => (isPartner ? sdk.partner.blogs.list(query) : sdk.admin.blogs.list(query)),
    ...options,
  });

  return { ...data, ...rest };
};

export const useCreateBlog = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminBlogCreateResponse>,
    FetchError,
    HttpTypes.AdminCreateBlog
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      isPartner ? sdk.partner.blogs.create(payload) : sdk.admin.blogs.create(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useDetailBlog = (
  id: string,
  options?: Omit<
    UseQueryOptions<HttpTypes.AdminBlogResponse, FetchError, HttpTypes.AdminBlogResponse, QueryKey>,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: blogQueryKeys.detail(id),
    queryFn: async () => (isPartner ? sdk.partner.blogs.detail(id) : sdk.admin.blogs.detail(id)),
    ...options,
  });

  return { ...data, ...rest };
};

export const useUpdateBlog = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminBlogUpdateResponse>,
    FetchError,
    HttpTypes.AdminUpdateBlog
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      isPartner ? sdk.partner.blogs.update(payload) : sdk.admin.blogs.update(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useDeleteBlog = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminBlogDeleteResponse>,
    FetchError,
    string
  >
) => {
  return useMutation({
    mutationFn: (id) => (isPartner ? sdk.partner.blogs.delete(id) : sdk.admin.blogs.delete(id)),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useInfiniteBlogs = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      HttpTypes.AdminBlogListInfiniteResponse,
      FetchError,
      HttpTypes.AdminBlogListInfiniteResponse,
      HttpTypes.AdminBlogListInfiniteResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey' | 'getNextPageParam' | 'initialPageParam'
  >
) => {
  return useInfiniteQuery({
    queryKey: blogPreviewQueryKeys.list(query),
    queryFn: async ({ pageParam = 1 }) => {
      const result = await (isPartner
        ? sdk.partner.blogs.list({ ...query, page: pageParam as number })
        : sdk.admin.blogs.list({ ...query, page: pageParam as number }));

      return {
        data: result?.data ?? [],
        totalCount: result?.totalCount ?? 0,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = lastPage?.data?.length ?? 0;
      const currentPage = allPages?.length ?? 0;

      if (currentPage * pageSize < Number(lastPage?.totalCount ?? 0)) {
        return currentPage + 1;
      }

      return undefined;
    },
    enabled: options?.enabled,
    ...options,
  });
};

export const useDuplicateCategoryBlog = (options: { onErrors: (error: Error) => void }) => {
  return useMutation({
    mutationFn: async (name: string) => sdk.admin.blogs.duplicate(name),
    onError(error) {
      options.onErrors(error);
    },
  });
};
